/** CRC32 查询表，供 Snowplow gzip 请求生成校验字段。 */
const CRC32_TABLE = createCrc32Table();

type SnowplowFetchInput = Parameters<typeof fetch>[0];
type SnowplowFetchInit = Parameters<typeof fetch>[1];
type SnowplowFetchBody = NonNullable<SnowplowFetchInit>['body'];
type SnowplowCompressionStream = {
  readonly readable: unknown;
  readonly writable: unknown;
};
type SnowplowCompressionStreamConstructor = new (format: 'gzip') => SnowplowCompressionStream;
type SnowplowCompressionWindow = Window & {
  CompressionStream?: SnowplowCompressionStreamConstructor;
};

/**
 * 生成 CRC32 查询表，避免每次计算时重复推导多项式。
 * @returns CRC32 查询表
 */
function createCrc32Table(): number[] {
  const table: number[] = [];

  for (let index = 0; index < 256; index += 1) {
    let value = index;

    for (let bit = 0; bit < 8; bit += 1) {
      value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }

    table[index] = value >>> 0;
  }

  return table;
}

/**
 * 计算字节数组的 CRC32，并返回 8 位十六进制字符串。
 * @param bytes - 待校验字节
 * @returns CRC32 十六进制字符串
 */
export function crc32Hex(bytes: Uint8Array): string {
  let crc = 0xffffffff;

  for (let index = 0; index < bytes.length; index += 1) {
    crc = CRC32_TABLE[(crc ^ bytes[index]) & 0xff] ^ (crc >>> 8);
  }

  return ((crc ^ 0xffffffff) >>> 0).toString(16).padStart(8, '0');
}

/**
 * 判断当前浏览器环境是否支持 Snowplow gzip customFetch 所需能力。
 * @returns true 表示可压缩 Snowplow POST 请求
 */
export function canUseSnowplowGzipFetch(): boolean {
  if (typeof window === 'undefined') return false;

  const CompressionStreamCtor = getCompressionStreamCtor();

  return Boolean(
    CompressionStreamCtor &&
    typeof Blob !== 'undefined' &&
    typeof TextEncoder !== 'undefined' &&
    typeof Response !== 'undefined' &&
    typeof btoa !== 'undefined',
  );
}

/**
 * 读取浏览器 CompressionStream 构造器，兼容类型环境缺少声明的情况。
 * @returns gzip 压缩流构造器
 */
function getCompressionStreamCtor(): SnowplowCompressionStreamConstructor | undefined {
  return (window as SnowplowCompressionWindow).CompressionStream;
}

/**
 * 将 fetch body 转为 Uint8Array。
 * @param body - Snowplow 请求体
 * @returns Uint8Array 或 null
 */
export async function bodyToBytes(
  body: SnowplowFetchBody | null | undefined,
): Promise<Uint8Array | null> {
  if (!body) return null;

  if (typeof body === 'string') {
    return new TextEncoder().encode(body);
  }

  if (body instanceof URLSearchParams) {
    return new TextEncoder().encode(body.toString());
  }

  if (body instanceof Blob) {
    return new Uint8Array(await body.arrayBuffer());
  }

  if (body instanceof ArrayBuffer) {
    return new Uint8Array(body);
  }

  return null;
}

/**
 * 判断 input 是否为 Request，并兼容 SSR 或缺少 Request 构造器的环境。
 * @param input - fetch 入参
 * @returns true 表示 input 是 Request
 */
function isRequestInput(input: SnowplowFetchInput): input is Request {
  return typeof Request !== 'undefined' && input instanceof Request;
}

/**
 * 按 fetch 覆盖语义读取请求体字节，init.body 存在时不再读取 Request 原 body。
 * @param input - fetch 入参
 * @param init - fetch 初始化参数
 * @returns 请求体字节或 null
 */
async function resolveBodyBytes(
  input: SnowplowFetchInput,
  init?: SnowplowFetchInit,
): Promise<Uint8Array | null> {
  if (init?.body != null) {
    return bodyToBytes(init.body);
  }

  if (!isRequestInput(input)) return null;

  const requestBody = await input.clone().arrayBuffer();
  return new Uint8Array(requestBody);
}

/**
 * gzip 压缩 Uint8Array。
 * @param bytes - 原始请求体字节
 * @returns gzip 后的字节
 */
async function gzipBytes(bytes: Uint8Array): Promise<Uint8Array> {
  const CompressionStreamCtor = getCompressionStreamCtor();
  if (!CompressionStreamCtor) return bytes;

  const arrayBuffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(arrayBuffer).set(bytes);
  const blobStream = new Blob([arrayBuffer]).stream();
  const stream = blobStream.pipeThrough(
    new CompressionStreamCtor('gzip') as Parameters<typeof blobStream.pipeThrough>[0],
  );
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/**
 * 将 Uint8Array 转为 base64 字符串，分段处理以避免大包体触发调用栈限制。
 * @param bytes - gzip 后的字节
 * @returns base64 字符串
 */
function bytesToBase64(bytes: Uint8Array): string {
  const chunkSize = 0x8000;
  let binary = '';

  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }

  return btoa(binary);
}

/**
 * 判断本次 fetch 是否是可被 gzip 包装的 POST 请求。
 * @param input - fetch 入参
 * @param init - fetch 初始化参数
 * @returns true 表示 POST 请求
 */
function isPostRequest(input: SnowplowFetchInput, init?: SnowplowFetchInit): boolean {
  const method = init?.method || (isRequestInput(input) ? input.method : 'GET');
  return method.toUpperCase() === 'POST';
}

/**
 * 合并 Request 与 init 上的 headers，并覆盖为 gzip 表单提交的 Content-Type。
 * @param input - fetch 入参
 * @param init - fetch 初始化参数
 * @returns gzip 表单请求头
 */
function resolveGzipHeaders(input: SnowplowFetchInput, init?: SnowplowFetchInit): Headers {
  const headers = new Headers(isRequestInput(input) ? input.headers : undefined);

  new Headers(init?.headers).forEach((value, key) => {
    headers.set(key, value);
  });
  headers.set('Content-Type', 'application/x-www-form-urlencoded');

  return headers;
}

/**
 * 为 Snowplow POST 请求创建 gzip customFetch，不满足条件或异常时回退原始 fetch。
 * @param baseFetch - 可选 fetch 实现，便于测试和兼容特殊运行环境
 * @returns 可传入 Snowplow newTracker 的 customFetch
 */
export function createSnowplowGzipFetch(baseFetch?: typeof fetch): typeof fetch {
  const resolvedBaseFetch =
    baseFetch || (typeof window !== 'undefined' ? window.fetch : globalThis.fetch);

  return async (input: SnowplowFetchInput, init?: SnowplowFetchInit): Promise<Response> => {
    try {
      if (!isPostRequest(input, init) || !canUseSnowplowGzipFetch()) {
        return resolvedBaseFetch(input, init);
      }

      const bytes = await resolveBodyBytes(input, init);
      if (!bytes?.length) {
        return resolvedBaseFetch(input, init);
      }

      const gzipBytesValue = await gzipBytes(bytes);
      const body = new URLSearchParams({
        gzip: '1',
        data: bytesToBase64(gzipBytesValue),
        crc: crc32Hex(gzipBytesValue),
      });
      const headers = resolveGzipHeaders(input, init);

      return resolvedBaseFetch(input, {
        ...init,
        body,
        headers,
        method: init?.method || 'POST',
      });
    } catch (error) {
      console.error('@snowplow--gzip-fetch', error);
      return resolvedBaseFetch(input, init);
    }
  };
}
